import dayjs from "../vendor/DayJs";

import * as Flags from "../Flags";

import { getAbsoluteUnixFromTimeRange } from "./DateUtils";
import * as Utils from "./Utils";
import * as PillarDataUtils from "./PillarDataUtils";

// TODO: Most if not all of this file should eventually be moved to the backend via a /view/ api -- JW, 11/2/19

/**
 * @deprecated
 */
export const getPillarData = (
	journeys,
	pillar,
	timeSelectionId,
	comparisonLevels,
) => {
	const keys = Array.from(
		new Set([pillar.data.stat, pillar.data.averageBy, "mileage"]),
	);

	const data = comparisonLevels.reduce((acc, level) => {
		acc[level] = journeys[level];
		return acc;
	}, {});

	const aggregateData = processData(data, keys, timeSelectionId);

	// in cases where there is no data for a driver because they have not
	// taken a trip yet we must pad the empty array out.
	// Therefore we must find the highest unit of time in the array (normally fleet)
	// that has data and pass this to the padding function.
	const unitOfTime =
		PillarDataUtils.getTimeSelectionById(timeSelectionId).grouping.unit;

	const maxTime = dayjs()
		.utc()
		.startOf(unitOfTime)
		.subtract(1, unitOfTime)
		.valueOf();

	// The data arrays which are padded with empty columns where
	// no data exists
	const scopedData = scopeDataColumns(aggregateData, timeSelectionId, maxTime);

	// initialise the final object
	const $ = {};

	comparisonLevels.forEach((level) => {
		const journeys = scopedData[level];
		journeys.forEach((value) => {
			// each score is calculated differently for each pillar metric.
			// As such, the functions getRisk() etc have passed their own
			// totalling functions which. This is called here
			let scoreTotal = null;
			if (value.total !== undefined) {
				scoreTotal = pillar.calc(value, level);
				if (isNaN(scoreTotal) || scoreTotal === Infinity) {
					scoreTotal = 0;
				}
			}

			// change into a human readable date value`
			// If there was no data and the column was padded
			// we must add that to the xaxis key for clarity.
			// At time of writting it is still unclear how this
			// should be shown
			const xAxisKey = getLabel(value.date, timeSelectionId);

			$[level] = Object.assign({}, $[level], {
				[xAxisKey]: {
					total: scoreTotal,
					businessMileage: value.mileage,
					level: level,
					liveUsers: value.liveUsers || 0,
				},
			});
		});
	});

	return $;
};

const processData = (data, keys, timeSelectionId, cutoff = true) => {
	const minTime = cutoff ? getAbsoluteUnixFromTimeRange(timeSelectionId) : 0;
	const totalKey = keys[0];

	const { unit } =
		PillarDataUtils.getTimeSelectionById(timeSelectionId).grouping;

	return Utils.objectMap(data, (level, journeys) => {
		return [
			level,
			Object.values(
				(journeys || []).reduce((journeys, journey) => {
					if (journey.businessTrips <= 0 || journey.date < minTime) {
						return journeys;
					}

					const timestamp = dayjs(journey.date).utc().startOf(unit);

					//journey.level = level;
					//journey.total = journey[totalKey];
					const acc =
						journeys[timestamp] ||
						(journeys[timestamp] = {
							date: timestamp,
							level: level,
							liveUsers: journey.liveUsers || 0,
							total: 0,
							...keys.reduce((acc, key) => {
								acc[key] = 0;
								return acc;
							}, {}),
						});

					acc.total += journey[totalKey];
					keys.forEach((key) => {
						acc[key] += journey[key];
					});

					return journeys;
				}, {}),
			),
		];
	});
};

export const metersToMiles = (meters) => {
	if (!meters) return meters;
	return Math.round(meters * 0.000621371);
};

export const metersToKilometers = (meters) => {
	if (!meters) return meters;
	return Math.round(meters / 1000);
};

const MONTH_LABEL_FORMAT = "MMM";
const WEEK_LABEL_FORMAT = "D MMM";

const getLabel = (value, timeSelectionId) => {
	const timeRange = PillarDataUtils.getTimeSelectionById(timeSelectionId);
	const debugFormat = Flags.DEBUG_FEATURES.is() ? "Z" : "";

	return value
		.clone()
		.utc()
		.startOf(timeRange.grouping.unit)
		.format(
			(timeRange.grouping.unit === "month"
				? MONTH_LABEL_FORMAT
				: WEEK_LABEL_FORMAT) + debugFormat,
		);
};

// Util function to create empty column slots for the weeks or
// months that are missing data. Complexity arises as we may need to
// pad for months or weeks. The returned data array should always be equal
// to or bigger than the input array.
export const scopeDataColumns = (data, timeSelectionId, maxTime) => {
	const timeRange = PillarDataUtils.getTimeSelectionById(timeSelectionId);

	// get the total number of columns the graph needs to satisfy UI req.
	const reqNoColumns = timeRange.grouping.blocks;

	return Utils.objectMap(data, (level, journeys) => {
		const columns = scopeArray(
			timeRange.grouping.unit,
			journeys,
			reqNoColumns,
			maxTime,
		);

		return [level, columns];
	});
};

const scopeArray = (timeSpan, series, reqColumns, maxTime) => {
	// initialize variables for the min and max of the time span
	const scopedSeries = [];
	let date = dayjs(maxTime).utc();

	while (scopedSeries.length < reqColumns) {
		// eslint-disable-next-line
		let actualData = series.find((dp) => dp.date.valueOf() === date.valueOf());

		if (actualData) {
			scopedSeries.push(actualData);
		} else {
			scopedSeries.push({
				date: date.clone(),
				total: undefined,
				liveUsers: 0,
			});
		}

		date = date.subtract(1, timeSpan);
	}

	// reorder the array by ascending week number
	// this array is now the total weeks needed
	return scopedSeries.sort((a, b) => a.date.valueOf() - b.date.valueOf());
};

export const getStat = (stat, statsObject) => {
	if (statsObject) {
		if (typeof statsObject.recent === "undefined") {
			return undefined;
		} else {
			const statObj = statsObject.recent[stat] || {};
			return statObj.score || undefined;
		}
	}
	return undefined;
};

/**
 * @deprecated
 */
export const getMultiSeriesChartData = (data, comparisonLevels) => {
	// Luckily the correct order happens to be in alphabetically in order
	comparisonLevels = comparisonLevels.sort();

	// the google chart data structure needs this first index in the array
	// to be the names of the columns then the data in the preceeding
	// array cells.

	const top = ["time", ...comparisonLevels];

	const _keys = new Set();
	Object.keys(data).forEach((comparisonLevel) => {
		const times = Object.keys(data[comparisonLevel]);
		const obj = {};
		times.forEach((time) => {
			_keys.add(time);
			obj[time] = data[comparisonLevel][time];
		});
		return (data[comparisonLevel] = obj);
	});

	// IMPORTANT - Array.from polyfill appearrs not to function correctly with Sets in IE11
	const _keysArray = [];
	_keys.forEach((k) => _keysArray.push(k));

	const _data = _keysArray.map((timeRange, i) => {
		// final xAxis string as displayed in the interface
		const _x = timeRange;

		// yAxis values as displayed in the graph
		const _y = comparisonLevels.map((level, j) => {
			const _level = Object.keys(data[level] || {})[i];

			const value =
				(data[level][_level] || {}).total !== undefined
					? data[level][_level].total
					: data[level][_level];

			return {
				...(typeof data[level][_level] === "object" ? data[level][_level] : {}),
				total: value,
			};
		});

		// concatenate the xAxis and yAxis values
		return [_x, ..._y];
	});

	// Pop the top array (array value labels) ontop of the series data
	// (x, y) to createthe final data structure required by the google charts api
	return [top, ..._data];
};

/**
 * TODO: Remove this and replace with API solution again
 *
 * @deprecated
 */
export const getStatSince = (journeys, pillar, from) => {
	const keys = Array.from(
		new Set([pillar.data.stat, pillar.data.averageBy, "mileage"]),
	);

	const sameDate = Date.now() - 1000;

	// Filter out journeys before 14 days + make all of them now so they don't get sorted out
	const data = Object.keys(journeys).reduce((acc, key) => {
		acc[key] = (journeys[key] || []).reduce((journeys, journey) => {
			if (journey.date < from) return journeys;
			journeys.push({ ...journey, date: sameDate });
			return journeys;
		}, []);
		return acc;
	}, {});

	const aggregateData = processData(data, keys, "4 weeks", false);

	return Object.keys(aggregateData).reduce((acc, level) => {
		const value = aggregateData[level][0];
		if (value) {
			acc[level] = pillar.calc(value, level);
		} else {
			acc[level] = null;
		}
		return acc;
	}, {});
};
